import { useState } from 'react'
import { actionTargets, type ActionOption, type TablePiece } from '@v2/sdk'
import { ActionList } from './ActionList'
import { FaceArt } from './FaceArt'
import { Icon } from './icons'

/** Cards inside a pile that a legal action can target right now. */
export function pileChoices(piece: TablePiece, actions: readonly ActionOption[]): ReadonlySet<string> {
  const targets = new Set(actions.flatMap(actionTargets))
  return new Set((piece.hidden ? [] : piece.contents ?? []).filter(item => targets.has(item.id)).map(item => item.id))
}

/**
 * A pile is browsed as a grid of card thumbnails. Cards a pending decision can use are
 * lit; opening one shows it large with its choices, so the grid never fills with buttons.
 */
export function PileBrowser({ piece, actions, onAction, disabled = false }: { piece: TablePiece; actions: readonly ActionOption[]; onAction: (action: ActionOption) => void; disabled?: boolean }) {
  const [query, setQuery] = useState('')
  const [opened, setOpened] = useState<string | null>(null)
  const [onlyChoices, setOnlyChoices] = useState(false)
  const contents = piece.hidden ? [] : piece.contents ?? []
  const actionsFor = (id: string) => actions.filter(action => actionTargets(action).includes(id))
  const choices = disabled ? new Set<string>() : pileChoices(piece, actions)
  const selected = contents.find(item => item.id === opened)
  const words = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean)
  const matches = (item: TablePiece) => words.every(word => (item.hidden ? 'Face-down card' : `${item.label} ${item.description ?? ''} ${item.face?.subtitle ?? ''} ${item.face?.body ?? ''}`).toLocaleLowerCase().includes(word))
  const filtered = contents.filter(item => matches(item) && (!onlyChoices || choices.has(item.id)))
  const pileActions = actionsFor(piece.id)

  if (selected) {
    const own = actionsFor(selected.id)
    // One card rarely needs its choices folded into groups; keep them as plain buttons.
    const flat = new Set(own.map(action => action.group)).size <= 1 ? own.map(({ group: _group, ...action }) => action) : own
    const label = selected.hidden ? 'Face-down card' : selected.label
    return <div className="tt-pile-detail">
      <button type="button" className="tt-pile-back" onClick={() => setOpened(null)}><Icon name="arrow" size={14} style={{ transform: 'rotate(180deg)' }}/>Back to pile</button>
      <div className="tt-pile-detail-art"><FaceArt art={selected.hidden ? selected.back : selected.face} label={selected.label} hidden={selected.hidden} color={selected.color}/></div>
      <div className="tt-pile-detail-copy">
        <h3>{label}</h3>
        {selected.hidden ? <p>This card is private.</p> : !selected.face?.image && selected.description ? <p>{selected.description}</p> : null}
      </div>
      {flat.length > 0
        ? <div className="tt-pile-detail-choices"><span className="tt-eyebrow">Choose</span><ActionList actions={flat} onAction={onAction} disabled={disabled} label={`${label} choices`}/></div>
        : <p className="tt-pile-detail-note">{choices.size ? 'No choice uses this card right now.' : 'Nothing to do with this card right now.'}</p>}
    </div>
  }

  return <div className="tt-pile-browser">
    {pileActions.length > 0 && <ActionList actions={pileActions} onAction={onAction} disabled={disabled} compact label={`${piece.label} actions`}/>}
    <label className="tt-choice-search"><span className="tt-sr-only">Search pile</span><input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Find a card…"/></label>
    <div className="tt-pile-meta">
      <span>{filtered.length === contents.length ? `${contents.length} ${contents.length === 1 ? 'card' : 'cards'}` : `${filtered.length} of ${contents.length}`} · top first</span>
      {choices.size > 0 && <button type="button" className={`tt-pile-filter${onlyChoices ? ' is-on' : ''}`} aria-pressed={onlyChoices} onClick={() => setOnlyChoices(value => !value)}><span className="tt-pile-dot" aria-hidden="true"/>{choices.size} can be chosen</button>}
    </div>
    <div className={`tt-pile-cards${choices.size ? ' has-choices' : ''}`}>
      {filtered.map(item => {
        const label = item.hidden ? 'Face-down card' : item.label
        const own = choices.has(item.id) ? actionsFor(item.id).length : 0
        return <button key={item.id} type="button" className={`tt-pile-card${own ? ' is-choice' : ''}`} aria-label={`Inspect ${label}`} title={label} onClick={() => setOpened(item.id)}>
          <FaceArt art={item.hidden ? item.back : item.face} label={item.label} hidden={item.hidden} color={item.color}/>
          {own > 0 && <span className="tt-pile-badge" aria-hidden="true">{own > 1 ? own : <Icon name="check" size={11}/>}</span>}
        </button>
      })}
    </div>
    {!filtered.length && <p className="tt-empty">{onlyChoices ? 'No choosable cards match.' : 'No matching cards.'}</p>}
  </div>
}
